import type { BillingAccount } from "@shared/lib/admin-billing";
import { formatCents } from "@shared/lib/format";
import { Avatar, AvatarFallback } from "@shared/components/ui/avatar";
import { Progress } from "@shared/components/ui/progress";
import { cn } from "@shared/lib/utils";

// Le tableau de bord Stripe bascule de lui-même entre test et production.
export const stripeCustomerUrl = (customerId: string) => `https://dashboard.stripe.com/customers/${customerId}`;

const SUBSCRIPTION_STATUS: Record<string, { label: string; tone: string }> = {
  active: { label: "Actif", tone: "active" },
  trialing: { label: "Essai", tone: "trial" },
  past_due: { label: "Paiement en relance", tone: "danger" },
  unpaid: { label: "Impayé", tone: "churned" },
  canceled: { label: "Résilié", tone: "neutral" },
  incomplete: { label: "Incomplet", tone: "neutral" },
  incomplete_expired: { label: "Expiré", tone: "neutral" },
  paused: { label: "En pause", tone: "neutral" },
};

/** État Stripe d'un compte, en un mot (et sa teinte de point). */
export function stripeState(account: BillingAccount): { label: string; tone: string } {
  if (!account.stripe_status) return { label: account.is_admin ? "Admin" : "Sans abonnement", tone: "lead" };
  if (account.cancel_at_period_end && account.stripe_status === "active") return { label: "Résiliation prévue", tone: "trial" };
  return SUBSCRIPTION_STATUS[account.stripe_status] ?? { label: account.stripe_status, tone: "neutral" };
}

/** Segment de filtre : les admins à part (l'API les traite en Pro). */
export const segmentOf = (account: BillingAccount) => (account.is_admin ? "admin" : account.plan_id);

export function displayName(account: BillingAccount) {
  return account.full_name.trim() || account.email;
}

/** Prix payé : « 49 € / mois », « 468 € / an », ou rien hors Stripe. */
export function priceLabel(account: BillingAccount) {
  if (account.price_cents === null || !["active", "past_due", "trialing"].includes(account.stripe_status ?? "")) return null;
  return `${formatCents(account.price_cents)} / ${account.billing_interval === "yearly" ? "an" : "mois"}`;
}

export function AccountAvatar({ account, className }: { account: BillingAccount; className?: string }) {
  return (
    <Avatar className={cn("size-8", className)}>
      <AvatarFallback className="bg-muted text-[12.5px] font-medium text-muted-foreground">
        {displayName(account).charAt(0).toUpperCase()}
      </AvatarFallback>
    </Avatar>
  );
}

/**
 * Consommation face à une limite du plan : « 3 / 3 » puis une jauge. La
 * jauge est une grandeur (rampe `--ordinal`) ; la limite atteinte passe en
 * avertissement — c'est là qu'un compte a besoin du plan suivant — et un
 * dépassement (plan réduit après coup) en anomalie.
 */
export function UsageMeter({ used, max, className }: { used: number; max: number; className?: string }) {
  const ratio = max > 0 ? used / max : 0;
  const tone = ratio > 1 ? "over" : ratio >= 1 ? "full" : "ok";
  return (
    <div className={cn("grid min-w-[92px] gap-1.5", className)}>
      <span className="flex items-baseline justify-between gap-2 text-[13px] tabular-nums">
        <span>
          {used} <span className="text-muted-foreground">/ {max}</span>
        </span>
        <span
          className={cn(
            "text-[12px] text-muted-foreground",
            tone === "full" && "text-warning",
            tone === "over" && "text-destructive",
          )}
        >
          {Math.round(ratio * 100)} %
        </span>
      </span>
      <Progress
        aria-label={`${used} sur ${max}`}
        className={cn(
          "h-1.5",
          "[&_[data-slot=progress-indicator]]:bg-ordinal-3",
          tone === "full" && "[&_[data-slot=progress-indicator]]:bg-warning",
          tone === "over" && "[&_[data-slot=progress-indicator]]:bg-destructive",
        )}
        value={Math.min(ratio, 1) * 100}
      />
    </div>
  );
}
